import { ECountry } from '@constants';
import { IUser } from '@sdk/contracts';
import { Col, DatePicker, Form, FormInstance, Input, Row } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  CountrySelector,
  GenderSelector,
  NationalitySelector,
  ProvincesExtendedSelector
} from '../Form';

import { SectionTitle } from './StudentFPGeneralInfoForm.styles';

import { useRequiredIfHadValue } from '@/hooks';

type StudentFPGeneralInfoFormProps = {
  form: FormInstance<IUser>;
};

export const StudentFPGeneralInfoForm: React.FC<StudentFPGeneralInfoFormProps> = ({ form }) => {
  const { t } = useTranslation();

  const birthCountry = Form.useWatch(['userInfo', 'countryBirth'], form);
  const isSpainBirth = birthCountry === ECountry.ES;

  const userId = Form.useWatch('_id', form);

  const { getFieldRules } = useRequiredIfHadValue({
    form,
    userId,
    excludeFields: [
      'hubspotId',
      ['userInfo', 'socialSecurityNumber'],
      ['userInfo', 'address'],
      ['userInfo', 'streetNumber'],
      ['userInfo', 'staircase'],
      ['userInfo', 'floor'],
      ['userInfo', 'door'],
      ['userInfo', 'postalCode']
    ]
  });

  if (!userId) {
    return null;
  }

  return (
    <>
      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item
            name={['userInfo', 'nationality']}
            label={t('NATIONALITY')}
            rules={getFieldRules(['userInfo', 'nationality'])}>
            <NationalitySelector placeholder={t('SELECT_ONE_OPTION')} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item
            name={['userInfo', 'socialSecurityNumber']}
            label={t('SOCIAL_SECURITY_NUMBER')}
            rules={[
              { required: false, message: t('DefaultTranslations:REQUIRED_FIELD') },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const isValid = /^\d{12}$/.test(String(value));
                  return isValid
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          t(
                            'StudentProfileConfigurationTranslations:SOCIAL_SECURITY_NUMBER_MUST_HAVE_12_DIGITS'
                          )
                        )
                      );
                }
              }
            ]}>
            <Input
              placeholder="000000000000"
              maxLength={12}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={e => {
                const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 12);
                form.setFieldValue(['userInfo', 'socialSecurityNumber'], onlyDigits);
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <SectionTitle>{t('BIRTH_DATA')}</SectionTitle>
      <Row gutter={8}>
        <Col xs={24} xl={12}>
          <Form.Item
            name="birthday"
            label={t('BIRTHDAY')}
            rules={getFieldRules('birthday')}
            getValueProps={value => ({ value: value ? dayjs(value) : null })}>
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              onChange={value => form.setFieldValue('birthday', value?.toDate())}
            />
          </Form.Item>
        </Col>
        <Col xs={24} xl={12}>
          <Form.Item
            name={['userInfo', 'gender']}
            label={t('SEX')}
            rules={getFieldRules(['userInfo', 'gender'])}>
            <GenderSelector placeholder={t('SELECT_ONE_OPTION')} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={8}>
        <Col xs={24} xl={24}>
          <Form.Item
            name={['userInfo', 'countryBirth']}
            label={t('BORN_COUNTRY')}
            rules={getFieldRules(['userInfo', 'countryBirth'])}>
            <CountrySelector disableDescription placeholder={t('SELECT_ONE_OPTION')} />
          </Form.Item>
        </Col>
      </Row>

      {isSpainBirth && (
        <>
          <Row gutter={8}>
            <Col xs={24} xl={12}>
              <Form.Item
                name={['userInfo', 'birthProvince']}
                label={t('PROVINCE')}
                rules={getFieldRules(['userInfo', 'birthProvince'])}>
                <ProvincesExtendedSelector />
              </Form.Item>
            </Col>
            <Col xs={24} xl={12}>
              <Form.Item
                name={['userInfo', 'birthMunicipality']}
                label={t('MUNICIPALITY')}
                rules={getFieldRules(['userInfo', 'birthMunicipality'])}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8}>
            <Col xs={24} xl={24}>
              <Form.Item
                name={['userInfo', 'birthLocality']}
                label={t('LOCALITY')}
                rules={getFieldRules(['userInfo', 'birthLocality'])}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      {birthCountry && !isSpainBirth && (
        <Row gutter={8}>
          <Col xs={24} xl={24}>
            <Form.Item
              name={['userInfo', 'birthLocalityForeign']}
              label={t('FOREIGN_BIRTH_LOCALITY')}
              rules={getFieldRules(['userInfo', 'birthLocalityForeign'])}>
              <Input placeholder={t('FOREIGN_BIRTH_LOCALITY_PLACEHOLDER')} />
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
};
